import { useEffect, useMemo, useState, type FC } from "react";
import { SecondaryButton } from "../SecondaryButton";
import "./PlotCard.less";
import { type PlotCardProps } from "../../../pages/new-trail/types";
import { BufferZonePopup } from "./popUp/bufferZonePopup";
import { TrialPlotsPopup } from "./popUp/trialPopup";
import { usePlotCardState } from "./utils";
import { RenderCards } from "./renderCards";
import { DeletePlotModal } from "../../../pages/new-trail/deletePlotModal";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import { useParams } from "react-router-dom";
import { StripsPopup } from "./popUp/StripsPopup";
import { AbLinePopup } from "./popUp/abLinePopup";
import {
  clearStripLinesCache,
  clearStripLinesFromMap,
} from "../../../maps/components/MapComponent/utils";
import { transformFromBackendFormat } from "./utils/optimizeStripsData";
import { formatTranslation } from "../../utils/translationUtils";
import { useTranslation } from "react-i18next";
import { PLOT_CARD_IDS } from "./constants";

export const PlotCard: FC<PlotCardProps> = ({ data, trialData }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeAbLinePopupId, setActiveAbLinePopupId] = useState<number | null>(
    null
  );
  const [viewMode, setViewMode] = useState<"plots" | "strips">("plots"); // Default to plots, will be updated if strips data exists

  const { newTrialState, setNewTrialState, trialProtocolData } =
    useNewTrialState();
  const { id } = useParams();

  /* strips-flow: identify strip/plot type
  check protocol details to identify and update isStripType flag
   */

  // Determine if this is a strip type based on protocol
  const isStripType = trialProtocolData?.trial_application_method === "strip";
  const isTrialPlotsCard = data.name === "Trial Plots";
  const isStripsView = isStripType && isTrialPlotsCard && viewMode === "strips";
  const {
    contextHolder,
    handleAddData,
    toggleVisibility,
    handleBufferZoneData,
    handleTrailPlotsData,
    deleteData,
    setDeleteData,
    handleDeleteData,
  } = usePlotCardState();

  const handleAddAbLine = (): void => {
    const createdAbLineId = handleAddData(data);
    setActiveAbLinePopupId(createdAbLineId);
  };

  // 🧪 TESTING: Monitor strips data changes and auto-switch to strips mode
  useEffect(() => {
    // Auto-switch to strips mode when strips data is detected
    if (newTrialState?.stripsData?.isStripMode && viewMode !== "strips") {
      setViewMode("strips");
    }
    // Only auto-switch to plots mode if we explicitly don't have strip mode data
    // and we're not in the middle of creating strips (avoid premature switching)
    else if (
      newTrialState?.stripsData &&
      !newTrialState.stripsData.isStripMode &&
      viewMode === "strips"
    ) {
      setViewMode("plots");
    }
  }, [newTrialState?.stripsData?.isStripMode, viewMode]);

  // Set mode based on protocol trial_application_method
  useEffect(() => {
    if (trialProtocolData?.trial_application_method) {
      const protocolMode =
        trialProtocolData.trial_application_method === "strip"
          ? "strips"
          : "plots";

      setViewMode(protocolMode);
    }
  }, [trialProtocolData?.trial_application_method]);

  // Determine effective view mode - now based on protocol instead of manual toggle
  const effectiveViewMode = (() => {
    // If protocol defines the mode, use it
    if (trialProtocolData?.trial_application_method) {
      return trialProtocolData.trial_application_method === "strip"
        ? "strips"
        : "plots";
    }
    // Otherwise use the current viewMode
    return viewMode ?? "plots";
  })();

  // Track if strip data has been initialized to prevent re-initialization
  const [stripDataInitialized, setStripDataInitialized] = useState(false);

  // Handle backend strips data when loading existing trials
  useEffect(() => {
    // Only initialize once when loading from backend
    // Skip if already initialized to prevent overwriting user edits
    if (stripDataInitialized) {
      return;
    }

    // If we get strip_design from backend, convert it to frontend format
    if (trialData?.trialData?.strip_design && trialData?.trialData?.ab_line) {
      try {
        // Convert backend format to full frontend format using transformation utility
        const frontendStripsData = transformFromBackendFormat(
          { strip_design: trialData.trialData.strip_design },
          trialData.trialData.ab_line
        );

        // Auto-switch to strips view when editing a trial with strips data
        setViewMode("strips");

        setNewTrialState((prevState: any) => ({
          ...prevState,
          stripsData: {
            ...frontendStripsData,
            appliedFromPopup: true, // treat backend-loaded strips as already applied
          },
          // Update AB-line selection based on strips data
          ab_line:
            prevState.ab_line?.map((abLine: any, index: number) => ({
              ...abLine,
              selectedForStrips:
                index === frontendStripsData.selectedAbLineIndex,
            })) || [],
        }));

        // Clear caches to ensure fresh calculation
        clearStripLinesCache();
        clearStripLinesFromMap();

        // Mark as initialized to prevent re-initialization
        setStripDataInitialized(true);
      } catch (error) {
        console.error("❌ Failed to convert backend strips data:", error);
      }
    }
  }, [
    trialData?.trialData?.strip_design,
    trialData?.trialData?.ab_line,
    stripDataInitialized,
    setNewTrialState,
    setViewMode,
  ]);

  useEffect(() => {
    const gridData: any = {
      id: new Date().getTime(),
      name: "Machinery Grid",
      key: "Machinery Grid",
      width: 50,
      rotation: 0,
      visible: true,
    };
    setNewTrialState((prevState: any) => ({
      ...prevState,
      machinery_grid: [gridData],
    }));
  }, []);

  useEffect(() => {
    if (id && trialData?.trialData?.machinery_grid) {
      const machineryGridData = {
        ...trialData.trialData.machinery_grid,
        key: "Machinery Grid",
        name: "Machinery Grid",
        visible: true,
        id: PLOT_CARD_IDS.MACHINERY_GRID,
      };

      setNewTrialState((prevState: any) => ({
        ...prevState,
        machinery_grid: [machineryGridData],
      }));
    }
    if (id && trialData?.trialData?.buffer_zone) {
      const bufferZoneData = {
        ...trialData.trialData.buffer_zone,
        key: "Buffer Zone",
        name: "Buffer Zone",
        visible: true,
        id: PLOT_CARD_IDS.BUFFER_ZONE,
      };

      setNewTrialState((prevState: any) => ({
        ...prevState,
        buffer_zone: [bufferZoneData],
      }));
    }
    if (id && Array.isArray(trialData?.trialData?.ab_line)) {
      // Ensure only one AB line is selected for strips (single selection)
      let hasSelectedAbLine = false;
      const abLineData = trialData.trialData.ab_line.map(
        (item: any, index: number) => {
          const newId = Number.parseInt(
            `${PLOT_CARD_IDS.AB_LINE_BASE}${index + 1}`
          );

          // Check if this AB line should be selected based on backend data or selectedAbLineId
          let shouldBeSelected = false;

          if (trialData?.trialData?.stripsData?.selectedAbLineId) {
            // Match by selectedAbLineId from strips data
            const targetId = trialData.trialData.stripsData.selectedAbLineId;
            shouldBeSelected =
              (newId === targetId ||
                item.id === targetId ||
                String(newId) === String(targetId)) &&
              !hasSelectedAbLine;
          } else {
            // Fall back to selectedForStrips flag
            shouldBeSelected = item.selectedForStrips && !hasSelectedAbLine;
          }

          if (shouldBeSelected) hasSelectedAbLine = true;

          return {
            ...item,
            key: "A/B Line + Machinery Grid",
            visible: true,
            // Enforce single selection: match by ID or fall back to first AB line
            selectedForStrips:
              shouldBeSelected || (!hasSelectedAbLine && index === 0),
            id: newId,
          };
        }
      );

      // Ensure exactly one AB line is selected
      if (!hasSelectedAbLine) {
        if (abLineData.length > 0) {
          abLineData[0].selectedForStrips = true;
        }
      }

      setNewTrialState((prevState: any) => ({
        ...prevState,
        ab_line: abLineData,
      }));
    }

    // Backend strips data loading is now handled in the useEffect above
    // No more localStorage manipulation needed

    if (id && Array.isArray(trialData?.trialData?.trial_plots)) {
      const trialPlotData = trialData.trialData.trial_plots.map(
        (item: any, index: number) => {
          const updatedReplicants =
            item.replicants?.map((replicant: any, repIndex: number) => ({
              ...replicant,
              parentId: item?.plot?.id || null,
              id: Number.parseInt(
                `${PLOT_CARD_IDS.TRIAL_PLOTS_BASE}${index + 1}${repIndex + 1}`
              ),
              visible: true,
              key: "trial_plots",
              name: `Replicant ${repIndex + 1}`,
            })) || [];

          return {
            ...item,
            plot: {
              ...item.plot,
              key: "Trial Plots",
              visible: true,
              id: Number.parseInt(
                `${PLOT_CARD_IDS.TRIAL_PLOTS_BASE}${index + 1}`
              ),
            },
            replicants: updatedReplicants,
          };
        }
      );

      setNewTrialState((prevState: any) => ({
        ...prevState,
        trial_plots: trialPlotData,
      }));
    }
  }, [id]);

  const cardsToRender = useMemo(() => {
    if (data.name === "A/B Line + Machinery Grid") {
      return (newTrialState?.ab_line ?? []).filter(
        (line: any) => line?.key === "A/B Line + Machinery Grid"
      );
    }

    const sourceCards = (newTrialState as any)[data.key as string];
    if (!Array.isArray(sourceCards) || sourceCards.length === 0) {
      return [];
    }

    return sourceCards.filter((val: any) => {
      if (val && typeof val === "object") {
        if ("plot" in val && val.plot.key === data.name) {
          return true;
        }
        if ("key" in val && val.key === data.name) {
          return true;
        }
      }
      return false;
    });
  }, [data.key, data.name, newTrialState]);

  return (
    <>
      {contextHolder}
      <div className="plot-card-main" data-testid="plot-card" key={data.id}>
        <div className="head-sec">
          {data.name !== "Machinery Grid" && (
            <div className="title-sec">
              <img src={data?.icon} alt="plot-icon" />
              <div>
                <h3 className="card-title">
                  {isStripsView ? "Strip" : data?.name}
                </h3>
                <p className="card-description">
                  {isStripsView
                    ? formatTranslation(t("trail.stripeDescription"))
                    : data?.description}
                </p>
                <span></span>
              </div>
            </div>
          )}

          {data.name === "A/B Line + Machinery Grid" && (
            <AbLinePopup
              open={Boolean(activeAbLinePopupId)}
              abLineId={activeAbLinePopupId}
              onClose={() => {
                // If the line still has the draft key, Apply was not clicked — discard it.
                setNewTrialState((prevState: any) => ({
                  ...prevState,
                  ab_line: (prevState.ab_line ?? []).filter(
                    (line: any) =>
                      !(
                        line.id === activeAbLinePopupId &&
                        line.key === "__AB_DRAFT__"
                      )
                  ),
                }));
                setActiveAbLinePopupId(null);
              }}
            >
              <Button
                className="plus-btn"
                onClick={handleAddAbLine}
                disabled={Boolean(activeAbLinePopupId)}
              >
                <SecondaryButton>+</SecondaryButton>
              </Button>
            </AbLinePopup>
          )}

          {data.name === "Buffer Zone" && (
            <BufferZonePopup
              onHandleData={handleBufferZoneData}
              openPopoverKey="openPopover"
              val={data.id}
              trialData={trialData}
            >
              <Button
                className={`plus-btn ${
                  newTrialState.buffer_zone?.length >= 1 ? "disabled" : ""
                }`}
                disabled={newTrialState.buffer_zone?.length >= 1}
              >
                <SecondaryButton>+</SecondaryButton>
              </Button>
            </BufferZonePopup>
          )}

          {/* {data.name === "Machinery Grid" && (
            <MachineryGridPopup
              openPopoverKey="openPopover"
              onHandleData={handleMachineGridData}
              val={data.id}
              trialData={trialData}
            >
              <Button
                className={`plus-btn ${
                  newTrialState.machinery_grid?.length >= 1 ? "disabled" : ""
                }`}
                disabled={newTrialState.machinery_grid?.length >= 1}
              >
                <SecondaryButton>+</SecondaryButton>
              </Button>
            </MachineryGridPopup>
          )} */}

          {data.name === "Trial Plots" && (
            <>
              {/* Toggle commented out - mode now determined by protocol */}
              {/* {renderModeToggle()} */}
              {isStripsView ? (
                <StripsPopup
                  openPopoverKey="openPopover"
                  onHandleData={handleTrailPlotsData}
                  val={data.id}
                >
                  <Button
                    className={`plus-btn ${
                      newTrialState?.stripsData?.stripDetails &&
                      newTrialState.stripsData.stripDetails.length > 0
                        ? "disabled"
                        : ""
                    }`}
                    disabled={
                      newTrialState?.stripsData?.stripDetails &&
                      newTrialState.stripsData.stripDetails.length > 0
                    }
                  >
                    <SecondaryButton>+</SecondaryButton>
                  </Button>
                </StripsPopup>
              ) : (
                <TrialPlotsPopup
                  openPopoverKey="openPopover"
                  onHandleData={handleTrailPlotsData}
                  val={data.id}
                >
                  <div className="plus-btn">
                    <SecondaryButton>+</SecondaryButton>
                  </div>
                </TrialPlotsPopup>
              )}
            </>
          )}
        </div>

        <div>
          {cardsToRender.length > 0 && (
            <RenderCards
              key={data.key}
              cards={cardsToRender}
              toggleVisibility={toggleVisibility}
              data={data}
              setIsModalOpen={setIsModalOpen}
              setDeleteData={setDeleteData}
              trialData={trialData}
              isStripType={isStripType}
              viewMode={effectiveViewMode}
              onViewModeChange={setViewMode}
            />
          )}
        </div>
      </div>
      <DeletePlotModal
        dataKey={data.key}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        data={deleteData}
        onDelete={handleDeleteData}
      />
    </>
  );
};
